<template>
  <f7-card class="padding-vertical-half padding-right tarjeta-domicilio" v-if="username">
    <f7-card-header>
      {{ $t('domicilio.titulo') }}
      <div class="cabecera-acciones">
        <span v-if="estadoFestivos" class="chip-estado" :class="'chip-' + estadoFestivos">
          {{ $t('festivos.estados.' + estadoFestivos) }}
        </span>
        <f7-button
          v-if="editable"
          small
          fill
          icon-f7="floppy_disk"
          :text="$t('domicilio.guardar')"
          class="display-flex"
          @click="guardar"
        ></f7-button>
      </div>
    </f7-card-header>
    <f7-card-content>
      <f7-block v-if="error403" strong class="no-margin">
        <p>{{ $t('domicilio.noAutorizado') }}</p>
      </f7-block>
      <!-- Sin ficha no hay formulario ni botón, así que un fallo pasajero de la primera carga
           dejaba la tarjeta inservible hasta salir y volver a abrir la ficha del empleado: no
           quedaba ninguna acción con la que repetir la petición
           (revisión CODEX del PR #1709). -->
      <f7-block v-else-if="cargaFallida" strong class="no-margin carga-fallida">
        <p>
          {{ $t('domicilio.sinCargar') }}
          <f7-link class="margin-left-half" @click="cargar()">{{ $t('domicilio.reintentar') }}</f7-link>
        </p>
      </f7-block>
      <r-form v-else-if="ficha" ref="rform" v-slot="{ rform }" :key="username" :initialData="ficha" :readonly="!editable">
        <f7-row no-gap class="list list-form no-hairlines">
          <f7-col width="100" medium="50">
            <ul>
              <r-input
                floating-label
                outline
                :label="$t('domicilio.direccion')"
                :form="rform"
                type="text"
                name="direccion"
              ></r-input>
            </ul>
          </f7-col>
          <f7-col width="100" medium="20">
            <ul>
              <r-input
                floating-label
                outline
                :label="$t('domicilio.codigoPostal')"
                :form="rform"
                type="text"
                name="codigoPostal"
              ></r-input>
            </ul>
          </f7-col>
          <f7-col width="100" medium="30">
            <ul>
              <!-- El municipio va como objeto, no como código suelto: con `async` el
                   autocomplete no precarga nada, y con un escalar se queda enseñando el INE
                   porque su búsqueda remota filtra por nombre y nunca resuelve el código. El
                   objeto lleva ya el nombre que devuelve la API. -->
              <r-autocomplete
                floating-label
                outline
                :dbAdapter="Municipio"
                :label="$t('domicilio.municipio')"
                :form="rform"
                name="municipio"
                labelProp="poblacion"
                prefixProp="codigo"
                idFieldDetail="id"
                :minLength="3"
                :async="true"
                :showNavigationLink="false"
                :shownFields="['codigo', 'poblacion', 'codProvincia', 'codComunidad']"
              ></r-autocomplete>
            </ul>
          </f7-col>

          <f7-col width="100" class="datos-derivados">
            <span>{{ $t('domicilio.provincia') }}: <b>{{ derivado(rform.formData.municipio, 'codProvincia') }}</b></span>
            <span>{{ $t('domicilio.comunidad') }}: <b>{{ derivado(rform.formData.municipio, 'codComunidad') }}</b></span>
            <!-- Provincia y comunidad son las del municipio elegido: se ven, no se escriben, para
                 que no puedan discrepar del código INE. El backend las vuelve a resolver al
                 guardar, así que lo que se ve aquí es un adelanto, no el dato bueno. -->
          </f7-col>

          <!-- Las ~15 personas con dos cuentas vinculadas (#52) tienen un solo domicilio, el de la
               cuenta que gestiona sus vacaciones: es el que mira Ajustes · Festivos. Sin decirlo,
               abrir la tarjeta por la otra cuenta parecería un domicilio distinto. -->
          <f7-col width="100" v-if="otraCuenta">
            <f7-block strong class="sugerencia no-margin-vertical">
              {{ $t('domicilio.cuentaCompartida') }} <b>{{ ficha.cuentaDomicilio }}</b>
            </f7-block>
          </f7-col>

          <f7-col width="100" v-if="ficha.municipioSugeridoIne && !codigoDe(rform.formData.municipio)">
            <f7-block strong class="sugerencia no-margin-vertical">
              {{ $t('domicilio.sugerencia') }}
              <b>{{ ficha.municipioSugeridoNombre }}</b> ({{ ficha.municipioSugeridoIne }})
              <span class="origen">· {{ ficha.origenSugerencia }}</span>
              <f7-button v-if="editable" small class="display-inline-block" @click="usarSugerencia(rform)">
                {{ $t('domicilio.usarSugerencia') }}
              </f7-button>
            </f7-block>
          </f7-col>

          <f7-col width="100" medium="30">
            <ul>
              <!-- DF1: los festivos son los de donde vive. La única excepción es elegir otro
                   municipio para esta persona, y entonces el motivo es obligatorio. -->
              <r-input
                :label="$t('domicilio.otroMunicipio')"
                :form="rform"
                type="check"
                name="excepcion"
              ></r-input>
            </ul>
          </f7-col>
          <f7-col width="100" medium="30" v-if="rform.formData.excepcion">
            <ul>
              <r-autocomplete
                floating-label
                outline
                :dbAdapter="Municipio"
                :label="$t('domicilio.municipioFestivos')"
                :form="rform"
                name="municipioFestivos"
                labelProp="poblacion"
                prefixProp="codigo"
                idFieldDetail="id"
                :minLength="3"
                :async="true"
                :showNavigationLink="false"
                :shownFields="['codigo', 'poblacion', 'codProvincia', 'codComunidad']"
              ></r-autocomplete>
            </ul>
          </f7-col>
          <f7-col width="100" medium="40" v-if="rform.formData.excepcion">
            <ul>
              <r-input
                floating-label
                outline
                :label="$t('domicilio.motivoExcepcion')"
                :form="rform"
                type="text"
                name="motivoMunicipioFestivos"
              ></r-input>
            </ul>
          </f7-col>
        </f7-row>

        <f7-block-title class="titulo-festivos">
          {{ $t('domicilio.festivosDe') }} {{ ficha.anio }}
          <f7-link class="link-anio" @click="cambiarAnio(-1)">◀</f7-link>
          <f7-link class="link-anio" @click="cambiarAnio(1)">▶</f7-link>
        </f7-block-title>
        <f7-list class="no-margin-vertical lista-festivos">
          <f7-list-item v-if="!ficha.municipioEfectivoIne" :title="$t('domicilio.sinMunicipio')"></f7-list-item>
          <f7-list-item v-else-if="!ficha.festivos || !ficha.festivos.length" :title="$t('domicilio.sinFestivos')"></f7-list-item>
          <f7-list-item
            v-for="festivo in ficha.festivos"
            :key="festivo.id"
            :title="festivo.fecha + ' · ' + festivo.nombre"
            :after="$t('festivos.ambitos.' + festivo.ambito)"
          >
            <f7-icon
              slot="media"
              size="14"
              :f7="festivo.estado === 'VALIDADO' ? 'checkmark_seal_fill' : 'clock'"
              :color="festivo.estado === 'VALIDADO' ? 'green' : 'orange'"
            ></f7-icon>
          </f7-list-item>
        </f7-list>
      </r-form>
    </f7-card-content>
  </f7-card>
</template>
<style scoped>
.tarjeta-domicilio .card-header { justify-content: space-between; font-weight: bold; }
.cabecera-acciones { display: flex; align-items: center; gap: 8px; }
.chip-estado {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  background-color: #8e8e93;
}
.chip-VALIDADO { background-color: #4cd964; }
.chip-PENDIENTE_VALIDAR { background-color: #ff9500; }
.chip-FALTAN_LOCALES { background-color: #d32f2f; }
.chip-FALTAN_COMPARTIDOS { background-color: #8e1b1b; }
.chip-SOBRAN_COMPARTIDOS { background-color: #7b1fa2; }
.chip-SOBRAN_LOCALES { background-color: #9c27b0; }
.chip-NO_CARGADO { background-color: #8e8e93; }
.datos-derivados { padding: 0 20px 8px; color: #555; font-size: 13px; }
.datos-derivados span { margin-right: 16px; }
.sugerencia { font-size: 13px; }
.sugerencia .origen { color: #8e8e93; }
.carga-fallida { color: #8e8e93; font-size: 13px; }
.titulo-festivos .link-anio { display: inline-block; margin-left: 8px; }
.lista-festivos .item-title { font-size: 13px; }
</style>
<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import rAutocomplete from "./../../components/rAutocomplete.vue";
import Municipio from "./../../js/db/Municipio.js";
import RhDomicilio from "./../../js/db/RhDomicilio.js";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";

export default {
  name: "rh-domicilio-card",
  components: {
    rForm,
    rInput,
    rAutocomplete
  },
  props: {
    username: {
      type: String
    },
    // El modo de la ficha que la contiene. La tarjeta se guarda por su cuenta, así que sin esto
    // la vista de solo lectura del empleado dejaba editar y guardar el domicilio.
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      Municipio,
      ficha: null,
      anio: null,
      // Lo que dice el servidor. Para saber si se puede escribir, `editable`.
      permisoEdicion: false,
      error403: false,
      // La carga falló por algo que no es un 403: no hay ficha que pintar, pero sí desde dónde
      // volver a intentarlo.
      cargaFallida: false,
      // La versión de la lectura de la que salen los datos que hay ahora en el formulario, que no
      // siempre es la de la última carga: `cargar()` refresca la ficha sin tocar formData.
      versionFormulario: null
    };
  },
  computed: {
    estadoFestivos: function() {
      return this.ficha ? this.ficha.estadoFestivos : null;
    },
    // Hacen falta las dos: el permiso del servidor manda sobre quién puede tocar el domicilio, y
    // el modo de la ficha sobre si esta pantalla se está editando o solo se está mirando.
    editable: function() {
      return this.permisoEdicion && !this.readonly;
    },
    // Si el domicilio que se está viendo vive en la otra cuenta de esta persona. Mayúsculas
    // aparte: el username llega de la ruta y la ficha lo devuelve como está en la ERP.
    otraCuenta: function() {
      var cuenta = this.ficha ? this.ficha.cuentaDomicilio : null;
      return !!cuenta && !!this.username && cuenta.toUpperCase() !== this.username.toUpperCase();
    }
  },
  watch: {
    username: function() {
      // La ficha de la persona anterior la tira el carril al pedir la de esta (#472, ver
      // `created`). Lo que queda aquí no es lo pintado sino el estado de la carga anterior, que
      // hay que retirar aunque aquella no llegara a pintar nada.
      this.cargaFallida = false;
      // El 403 era de la persona anterior: dejándolo puesto, un fallo pasajero de la nueva pintaba
      // «No autorizado» —que manda sobre `cargaFallida`— y escondía el enlace de reintentar, sin
      // que nada volviera a borrarlo. Mismo olvido que tenía la tarjeta de funciones (#51).
      this.error403 = false;
      this.cargar();
    }
  },
  created: function() {
    // Un carril: cambiar de persona o de año lanza otra petición y la vieja puede llegar después,
    // así que solo contesta la última (ver carrilPeticiones). Fuera de `data` porque no es estado
    // que se pinte.
    //
    // #472 · Y es el carril quien tira lo de la persona anterior al pedir la de otra, en vez del
    // `watch`: la ficha en pantalla es la de la persona anterior, y dejarla puesta mientras carga
    // la nueva deja un r-form recreado (:key="username") con datos ajenos, y Guardar los mandaría
    // a esta persona. Sin ficha no hay formulario ni botón. Cambiar de AÑO no la tira: la cabecera
    // dice el año de la ficha y no el pedido, así que no hay nada que no cuadre, y vaciarla
    // borraría el formulario que se está editando (ver `cargar`).
    const self = this;
    this.carril = carrilPeticiones({
      vaciar: function() {
        self.ficha = null;
        self.permisoEdicion = false;
        self.versionFormulario = null;
      }
    });
    this.cargar();
  },
  methods: {
    es403: function(e) {
      return this.estadoHttp(e) == 403;
    },
    // 409: alguien ha guardado este domicilio mientras se editaba. El servidor no ha escrito
    // nada, así que lo que hay en la ficha es lo del otro.
    es409: function(e) {
      return this.estadoHttp(e) == 409;
    },
    estadoHttp: function(e) {
      return (e && (e.status || (e.xhr && e.xhr.status))) || 0;
    },
    // La llama la ficha de empleado tras guardarla: la sugerencia de municipio y el estado de los
    // festivos se calculan en el servidor a partir de datos que se editan fuera de esta tarjeta
    // (la dirección de inicio de jornada), así que hay que volver a pedirlos aunque no haya
    // cambiado la persona. No se borra `ficha`: el formulario conserva lo que haya escrito quien
    // esté editando, que este guardado no ha tocado.
    //
    // La llama también cuando se guarda la tarjeta de «Vacaciones», con
    // `motivo = 'cuentaGestora'`: el check «Gestiona vacaciones en este usuario» decide cuál de
    // las cuentas de la persona manda sobre su domicilio (`RhCuentasPersona.porMando`), así que
    // moverlo cambia la cuenta que esta tarjeta está leyendo. Sin releer, seguía enseñando el
    // domicilio de la otra cuenta como si fuera el de esta.
    recargar: function(motivo) {
      this.cargar(false, motivo);
    },
    // `silencioso` lo pone el reintento tras un 409: el diálogo del error ya ha explicado que el
    // domicilio lo cambió otro, y el aviso de la recarga encima sería decirlo dos veces.
    // `motivo` sale en el aviso cuando el formulario se rehace por algo que no es «lo ha cambiado
    // otra persona»; ver `sincronizarFormulario`.
    cargar: function(silencioso, motivo) {
      var self = this;
      if (!self.username) return;
      self.carril.lanzar(RhDomicilio.get(self.username, self.anio), {
        // De quién es, y solo eso: con otra persona pintada el carril vacía (#472, ver `created`).
        clave: [self.username],
        exito: function(res) {
          self.error403 = false;
          self.cargaFallida = false;
          self.ficha = self.normalizar(res);
          self.anio = self.ficha.anio;
          self.permisoEdicion = res && res.editable === true;
          self.sincronizarFormulario(silencioso, motivo);
        },
        limpiar: function() {
          // Las flechas cuentan desde `anio`, pero la cabecera sigue enseñando el año de la
          // ficha: si no se vuelve a él, avanzar otra vez se saltaría el año que ha fallado y no
          // habría forma de reintentarlo (revisión CODEX del PR #1709).
          self.anio = self.ficha ? self.ficha.anio : null;
          // Y sin ficha no hay formulario: hace falta un sitio desde donde repetir la petición, o
          // la tarjeta se queda inservible hasta salir y volver a abrir la ficha del empleado.
          self.cargaFallida = !self.ficha;
        },
        fallo: function(e) {
          if (self.es403(e)) {
            self.error403 = true;
            self.ficha = null;
            // Sin ficha no hay año en pantalla ni flechas: el siguiente intento vuelve a pedir el
            // año que decida el servidor. Y aquí no se ofrece reintentar: el permiso no cambia por
            // volver a pedirlo.
            self.anio = null;
            self.cargaFallida = false;
            return;
          }
          // Un fallo que NO es de permisos: si venía un 403 de antes hay que quitarlo, o la
          // plantilla sigue pintando «No autorizado» —que va primero— y esconde el reintento.
          self.error403 = false;
          self.$f7.dialog.alert(e && e.message ? e.message : self.$t('domicilio.errorCarga'));
        }
      });
    },
    /**
     * Ata el formulario a la lectura de la que salen sus datos.
     *
     * Al guardar se manda `versionFormulario`, no la de la última carga: `cargar()` refresca la
     * ficha sin tocar formData —para no borrar lo que se esté escribiendo—, así que mandar la
     * versión recién leída con los campos viejos colaría el guardado por encima del cambio de la
     * otra persona justo cuando el control de concurrencia tenía que pararlo: el 409 se convertía
     * en «dale a Guardar dos veces» (revisión CODEX del PR #1709).
     *
     * Por eso, cuando la lectura trae una versión distinta de la del formulario, el formulario se
     * rehace con lo que hay guardado: es lo que vale de verdad, y quien esté editando lo ve y
     * decide si repetir su cambio. Mientras la versión sea la misma, lo escrito se respeta.
     */
    sincronizarFormulario: function(silencioso, motivo) {
      var rform = this.$refs.rform;
      // Todavía no hay formulario —se crea con `initialData` cuando aparece la ficha— o es la
      // primera lectura de esta persona: lo que se pinte sale de esta ficha.
      if (!rform || this.versionFormulario === null) {
        this.versionFormulario = this.ficha.version;
        return;
      }
      if (this.ficha.version === this.versionFormulario) return;
      rform.formData = this.ficha;
      this.versionFormulario = this.ficha.version;
      if (silencioso) return;
      this.$f7.toast
        .create({
          // Con el check movido no lo ha cambiado nadie: lo que ha cambiado es la cuenta de la que
          // sale, y decir «otra persona ha cambiado este domicilio» mandaría a buscar un cambio
          // que no existe.
          text: this.$t(motivo === 'cuentaGestora'
            ? 'domicilio.recargadoPorCuentaGestora' : 'domicilio.recargadoPorOtro'),
          position: 'center',
          closeTimeout: 4000
        })
        .open();
    },
    // El check «otro municipio» no existe en el backend: es la forma de enseñar la excepción en
    // pantalla. Lo que se guarda es el municipio de festivos, o nada. Los dos municipios se
    // convierten en objeto porque es lo que el autocomplete asíncrono sabe mostrar sin buscar.
    normalizar: function(res) {
      var ficha = Object.assign({
        direccion: null,
        codigoPostal: null,
        municipioIne: null,
        municipioFestivosIne: null,
        motivoMunicipioFestivos: null,
        festivos: []
      }, res || {});
      ficha.municipio = ficha.municipioIne
        ? {
            codigo: ficha.municipioIne,
            poblacion: ficha.municipioNombre,
            codProvincia: ficha.codProvincia,
            codComunidad: ficha.codComunidad
          }
        : null;
      ficha.municipioFestivos = ficha.municipioFestivosIne
        ? { codigo: ficha.municipioFestivosIne, poblacion: ficha.municipioFestivosNombre }
        : null;
      ficha.excepcion = !!ficha.municipioFestivosIne;
      return ficha;
    },
    // El autocomplete deja `{}` al borrar el texto, no `null`: sin código no hay municipio.
    codigoDe: function(municipio) {
      return municipio && municipio.codigo ? municipio.codigo : null;
    },
    derivado: function(municipio, campo) {
      return (municipio && municipio[campo]) || '—';
    },
    // Se cuenta desde el año ya pedido, no desde el de la ficha en pantalla: mientras la carga
    // está en vuelo `ficha` sigue siendo la del año anterior, así que dos flechas seguidas
    // pedían dos veces el mismo año e ir y volver deprisa acababa un año más atrás
    // (revisión CODEX del PR #1709).
    cambiarAnio: function(delta) {
      var base = this.anio || (this.ficha && this.ficha.anio) || new Date().getFullYear();
      this.anio = base + delta;
      this.cargar();
    },
    // El domicilio se ha guardado: se dice igual se vaya a pintar la respuesta o se vaya a releer
    // el año que ahora hay en pantalla. Callarlo en el segundo caso dejaría a quien guardó sin
    // saber si su cambio entró.
    avisarGuardado: function() {
      this.$f7.toast
        .create({
          text: this.$t('domicilio.guardado'),
          position: 'center',
          closeTimeout: 2000,
          icon: '<i class="f7-icons">checkmark_alt</i>'
        })
        .open();
    },
    usarSugerencia: function(rform) {
      rform.formData.municipio = {
        codigo: this.ficha.municipioSugeridoIne,
        poblacion: this.ficha.municipioSugeridoNombre
      };
    },
    guardar: function() {
      var self = this;
      var app = self.$f7;
      // El botón no está cuando no se puede editar, pero el guardado es el único camino que
      // escribe: que no dependa de que el botón siga escondido.
      if (!self.editable) return;
      if (!self.$refs.rform) return;
      var data = JSON.parse(JSON.stringify(self.$refs.rform.formData));
      // Al backend van los códigos INE, no los objetos del autocomplete.
      data.municipioIne = self.codigoDe(data.municipio);
      data.municipioFestivosIne = self.codigoDe(data.municipioFestivos);
      delete data.municipio;
      delete data.municipioFestivos;
      // El año viaja por query, no en el cuerpo: el de formData puede estar caducado.
      delete data.anio;
      // La versión que se manda es la de la lectura de la que salen estos datos, no la de la
      // última carga: con la recién leída el servidor daría por bueno un formulario que no vio lo
      // que guardó el otro (ver `sincronizarFormulario`).
      data.version = self.versionFormulario;
      if (!data.excepcion) {
        data.municipioFestivosIne = null;
        data.motivoMunicipioFestivos = null;
      } else if (!data.municipioFestivosIne) {
        app.dialog.alert(self.$t('domicilio.faltaMunicipioExcepcion'));
        return;
      } else if (!data.motivoMunicipioFestivos) {
        // El backend lo rechaza igual; avisar aquí ahorra el viaje y explica por qué.
        app.dialog.alert(self.$t('domicilio.faltaMotivo'));
        return;
      }
      // El PUT también puede llegar tarde: si mientras iba se ha cambiado de persona, lo
      // guardado es de la anterior y aplicarlo pondría su ficha bajo el nombre de la nueva.
      var usuario = self.username;
      // El año no puede salir de formData: el r-form solo se recrea al cambiar de persona
      // (:key="username") y no re-sincroniza initialData, así que después de usar las flechas
      // conserva el año de la primera carga. El bueno es el de la cabecera.
      var anio = self.anio;
      // #663 · La espera de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que la apagaba.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhDomicilio.guardar(usuario, data, anio)
        .then(function(res) {
          // Si mientras iba el PUT se ha cambiado de persona, lo guardado es de la anterior:
          // aplicarlo pondría su ficha bajo el nombre de la nueva, y la nueva ya tiene su propia
          // carga en marcha (revisión CODEX del PR #1709).
          if (usuario !== self.username) return;
          self.avisarGuardado();
          if (anio !== self.anio) {
            // Solo se ha cambiado de año. La respuesta trae los festivos del año anterior y no se
            // puede pintar, pero tampoco basta con descartarla: el domicilio YA está guardado, y
            // la tarjeta se quedaba con el calendario y la versión de antes del guardado, así que
            // el siguiente Guardar chocaba en 409 y los festivos podían ser los del municipio
            // viejo. Se vuelve a pedir el año que está en pantalla, que es el que ahora hay que
            // enseñar (revisión CODEX del PR #1709).
            self.cargar(true);
            return;
          }
          // Lo guardado manda sobre cualquier carga que siga en vuelo.
          self.carril.invalidar();
          self.cargaFallida = false;
          self.ficha = self.normalizar(res);
          self.anio = self.ficha.anio;
          if (self.$refs.rform) {
            self.$refs.rform.formData = self.ficha;
          }
          // El formulario vuelve a ser el de esta lectura, así que el siguiente guardado sale con
          // su versión.
          self.versionFormulario = self.ficha.version;
        })
        .catch(function(e) {
          // Aquí no se mira el año: el error solo avisa de que no se guardó, no toca la ficha ni
          // el año en pantalla, y callarlo por haber cambiado de año perdería el aviso.
          if (usuario !== self.username) return;
          app.dialog.alert(e && e.message ? e.message : self.$t('domicilio.errorGuardar'));
          // Con el choque de versiones hay que volver a leer: la tarjeta —y el formulario, que
          // ahora se sincroniza con ella— enseña lo que guardó el otro, y sobre eso se decide si
          // repetir el cambio. Sin esto, repetir el guardado chocaría siempre con la misma
          // versión vieja y no habría forma de salir.
          if (self.es409(e)) self.cargar(true);
        })
        .then(function() {
          espera.cerrar();
        });
    }
  }
};
</script>
